import CloseIcon from "@mui/icons-material/Close"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    CircularProgress,
    Typography,
    Box,
    useTheme,
    IconButton,
} from "@mui/material"
import { ReactElement, useState, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { LCTOnlyDatasets } from "../../../constants/enums"
import { SPLIT_PHASE_ASSUMED } from "../../../constants/generalConstants"
import { useDemandSearchURLFilterParams } from "../../../hooks/useDemandDeriveFilterValuesFromUrl"
import { useGenSearchURLFilterParams } from "../../../hooks/useGenDeriveFilterValuesFromUrl"
import { useGetState } from "../../../hooks/useGetState"
import {
    getShowSearchFilters,
    setShowSearchFilters,
} from "../../../redux/preferencesSlice"
import { IDemDatabaseSearchResult } from "../../../services/middleware/mappers/searchDemandDevicesMapper"
import { IDeviceSearchResult } from "../../../services/middleware/types/singleApplication/searchLctTypes"
import DemandDeviceSearch from "../../../webpages/lowCarbonTechDatabases/demandDeviceSearch"
import GenDeviceSearch from "../../../webpages/lowCarbonTechDatabases/genDeviceSearch"
import { SupplyPhases } from "../../submitApplication/single/types/formGeneratorTypes"
import { IDatabaseSearchResult as IGenDatabaseSearchResult } from "../../tables/ttr/genSearchResults"

interface IDeviceSearchPopupProps {
    open: boolean
    onClose: () => void
    searchParameters: {
        deviceType: string
        dataset: string
        defaultFilters?: Record<string, any>
        availableDatasets?: string[]
    }
    phases: SupplyPhases
    onDeviceSelected: (device: IDeviceSearchResult) => void
    error?: string | null
}

/**
 * Popup component for advanced device search.
 * Displays a table view of devices with filters and allows selection.
 */
const DeviceSearchPopup = ({
    open,
    onClose,
    searchParameters,
    phases,
    onDeviceSelected,
    error: errorProp,
}: IDeviceSearchPopupProps): ReactElement => {
    const theme = useTheme()
    const [internalError, setInternalError] = useState<string | null>(null)
    const [isSelecting, setIsSelecting] = useState<boolean>(false)
    const showFilters = useGetState(getShowSearchFilters)
    const dispatch = useDispatch()
    const [searchParams, setSearchParams] = useSearchParams()

    // Get the clearAllFilters functions from both hooks
    const { clearAllFilters: clearDemandFilters } =
        useDemandSearchURLFilterParams(
            searchParams,
            setSearchParams,
            true // Always true in popup mode
        )

    const { clearAllFilters: clearGenFilters } = useGenSearchURLFilterParams(
        searchParams,
        setSearchParams,
        undefined,
        true // Always true in popup mode
    )

    const error = errorProp !== undefined ? errorProp : internalError

    const [currentDataset, setCurrentDataset] = useState<string>(
        searchParameters?.dataset || LCTOnlyDatasets.HP
    )

    const isDemandDevice = useMemo(
        () =>
            [
                LCTOnlyDatasets.HP,
                LCTOnlyDatasets.EVAC,
                LCTOnlyDatasets.EVDC,
            ].includes(currentDataset as LCTOnlyDatasets),
        [currentDataset]
    )

    const handleDatasetChange = useCallback((dataset: string) => {
        setCurrentDataset(dataset)
    }, [])

    const formatDemandDeviceSelected = useCallback(
        (searchResult: IDemDatabaseSearchResult): IDeviceSearchResult => {
            try {
                return {
                    displayValue: `${searchResult.manufacturer} ${searchResult.modelRef}`,
                    deviceRef: searchResult.deviceReference || "",
                    deviceModelNo: searchResult.modelRef || "",
                    deviceManufacturer: searchResult.manufacturer || "",
                    deviceSize: Number(searchResult.maxDemand) || 0,
                    deviceSizeName: "Max Demand",
                    deviceSizeUnit: "A",
                    devicePhases: searchResult.phases || "",
                }
            } catch (err) {
                return {} as IDeviceSearchResult
            }
        },
        []
    )

    /**
     * Formats the generation device search result to match the expected format
     * used by the form context.
     */
    const formatGenDeviceSelected = useCallback(
        (searchResult: IGenDatabaseSearchResult): IDeviceSearchResult => {
            try {
                return {
                    displayValue: `${searchResult.manufacturer} ${searchResult.model}`,
                    deviceRef: searchResult.deviceReference || "",
                    deviceModelNo: searchResult.model || "",
                    deviceManufacturer: searchResult.manufacturer || "",
                    deviceSize: parseFloat(searchResult.capacity) || 0,
                    deviceSizeName: "Capacity",
                    deviceSizeUnit: "kW",
                    devicePhases: searchResult.phases?.toString() || "",
                }
            } catch (err) {
                return {} as IDeviceSearchResult
            }
        },
        []
    )

    /**
     * Handle selection of a demand device
     */
    const handleDemandDeviceSelect = useCallback(
        (device: IDemDatabaseSearchResult) => {
            try {
                setIsSelecting(true)
                const formattedDevice = formatDemandDeviceSelected(device)

                onDeviceSelected(formattedDevice)
                onClose()
            } catch (err) {
                setInternalError("Failed to select device")
            } finally {
                setIsSelecting(false)
            }
        },
        [formatDemandDeviceSelected, onDeviceSelected, onClose]
    )

    /**
     * Handle selection of a generation device
     */
    const handleGenDeviceSelect = useCallback(
        (device: IGenDatabaseSearchResult) => {
            try {
                setIsSelecting(true)
                const formattedDevice = formatGenDeviceSelected(device)

                onDeviceSelected(formattedDevice)
                onClose()
            } catch (err) {
                setInternalError("Failed to select device")
            } finally {
                setIsSelecting(false)
            }
        },
        [formatGenDeviceSelected, onDeviceSelected, onClose]
    )

    // Function to clear all filters based on current device type
    const clearFilters = useCallback(() => {
        if (isDemandDevice) {
            clearDemandFilters()
        } else {
            clearGenFilters()
        }
    }, [isDemandDevice, clearDemandFilters, clearGenFilters])

    // Handle dialog close safely
    const handleClose = useCallback(() => {
        if (!isSelecting) {
            onClose()
        }
        if (showFilters) {
            // If the filters are open, close them
            dispatch(setShowSearchFilters())
        }
        // Clear all URL query parameters when closing
        clearFilters()
    }, [isSelecting, onClose, dispatch, showFilters, clearFilters])

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            disableEscapeKeyDown={isSelecting}
            PaperProps={{
                sx: {
                    height: "90vh",
                    maxHeight: "900px",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 1,
                    overflow: "hidden",
                },
            }}
        >
            {/* Header styled like the screenshot */}
            <DialogTitle
                component="div"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    p: 0,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                {/* Main header with title and close button */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        p: 2,
                    }}
                >
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontWeight: "bold",
                        }}
                    >
                        <span style={{ color: theme.palette.text.secondary }}>
                            Advanced Device Search
                        </span>
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {isSelecting && (
                            <CircularProgress size={24} sx={{ mr: 2 }} />
                        )}
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                }}
            >
                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box
                    sx={{
                        p: 0,
                        overflowY: "auto",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {isDemandDevice ? (
                        <DemandDeviceSearch
                            onDeviceSelect={handleDemandDeviceSelect}
                            onDatasetChange={handleDatasetChange}
                            searchConfig={{
                                deviceType: searchParameters?.deviceType || "",
                                dataset: currentDataset,
                                ...(phases !== SPLIT_PHASE_ASSUMED && {
                                    phases: phases || 3,
                                }),
                                isPopupMode: true,
                            }}
                            // Pass the shared filter visibility state
                            showFilters={showFilters}
                        />
                    ) : (
                        <GenDeviceSearch
                            onDeviceSelect={handleGenDeviceSelect}
                            searchConfig={{
                                deviceType: searchParameters?.deviceType || "",
                                dataset: currentDataset,
                                isPopupMode: true,
                            }}
                            // Pass the shared filter visibility state
                            showFilters={showFilters}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    p: 2,
                    bgcolor: theme.palette.background.paper,
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    onClick={handleClose}
                    color="primary"
                    variant="contained"
                    disabled={isSelecting}
                    sx={{
                        borderRadius: 0.5,
                        textTransform: "none",
                        px: 3,
                        fontWeight: "bold",
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

DeviceSearchPopup.defaultProps = {
    error: null,
}

export default DeviceSearchPopup
